import { makeStyles } from '@material-ui/core';
import { Callout } from 'copilot-design-system';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { shallowEqual, useSelector } from 'react-redux';

import { Contract, ContractField } from 'src/entities/Contract';
import { useGetContractInputValue } from 'src/hooks/useGetContractInputValue';
import { signaturePageContentWidth } from 'src/legacy/components/Signature/SignaturePageContent';
import { FieldType, InputType } from 'src/services/api';
import { useCustomFields } from 'src/services/api/customFieldsApi';
import { RootState } from 'src/store';
import { CustomFieldEntityType } from 'src/store/clients/types';
import {
  SignaturePageComponent,
  SignaturePageImageData,
} from 'src/store/signaturePage/types';
import { RegularCardShadow } from 'src/theme/shadows';
import { S3Utils } from 'src/utils';
import {
  getComponentNameByComponentType,
  getComponentTypeUsingContractType,
} from 'src/utils/ContractUtils';

import {
  ContractDraggableItem,
  ContractDragItemType,
} from '../Signature/ContractDraggableItem';
import { SignaturePageImage } from '../Signature/SignaturePageImage';
import { PDFSkeleton } from '../Skeleton/PDFSkeleton';

const useStyles = makeStyles(() => ({
  root: {
    width: '100%',
    maxWidth: signaturePageContentWidth,
    margin: '0px auto',
  },
  container: {
    position: 'relative',
    width: '100%',
  },
  page: {
    pointerEvents: 'none',
    marginBottom: 15,
    borderRadius: 4,
    overflow: 'hidden',
    boxShadow: RegularCardShadow,
    '& img': {
      width: '100%',
    },
  },
  fieldsContainer: {
    position: 'absolute',
    inset: 0,
  },
  field: {
    display: 'flex',
    alignItems: 'center',
    position: 'absolute',
  },
  signatureImage: {
    height: '100%',
    width: '100%',
    objectFit: 'fill',
  },
}));

export const isContractField = (
  field: SignaturePageComponent | null,
): field is SignaturePageComponent => field !== null;

export const PendingContract = ({ contract }: { contract: Contract }) => {
  const { t } = useTranslation();
  const classes = useStyles({});
  const [pageImages, setPageImages] = useState<Array<SignaturePageImageData>>(
    [],
  );

  const customFields = useCustomFields(CustomFieldEntityType.CLIENT);

  const { clients, companies, activeCompanyId } = useSelector(
    (state: RootState) => ({
      clients: state.clients.clients,
      companies: state.clients.companies,
      activeCompanyId: state.activeCompany.activeCompanyId,
    }),
    shallowEqual,
  );

  const getContractFieldValue = useGetContractInputValue(
    contract,
    clients.find((c) => c.id === contract.recipientId),
    companies,
    customFields,
    activeCompanyId || undefined,
  );

  const handleLoadPageImages = async () => {
    const { pageKeys, identityId } = contract;
    const images = await Promise.all(
      pageKeys.map(async (key, index) => {
        const s3ImageURL = await S3Utils.getFile(key, {
          identityId,
          level: 'protected',
        });
        return {
          key,
          url: s3ImageURL,
          pageNumber: index + 1,
          loaded: false,
        };
      }),
    );

    setPageImages(images);
  };

  useEffect(() => {
    handleLoadPageImages();
  }, []);

  const pageComponents: SignaturePageComponent[] = useMemo(() => {
    const formattedContractFields = (contract.fields || [])
      .map((field: ContractField): SignaturePageComponent | null => {
        const value = getContractFieldValue(field);

        // Remove AutoFill inputs for which the client doesn't have a value
        if (!value && field.inputType === InputType.AutoFill) return null;

        return {
          fieldType: field.type,
          inputType: field.inputType,
          autoFillField: field.autoFillField,
          xPosition: field.bounds.x,
          yPosition: field.bounds.y,
          label: field.label,
          receiverUserId: contract.recipientId,
          height: field.bounds.height,
          width: field.bounds.width,
          isAgree: false,
          key: field.id,
          resizedHeight: field.bounds.resizedHeight,
          resizedWidth: field.bounds.resizedWidth,
          value,
          page: field.page,
          componentType: getComponentTypeUsingContractType(
            field,
            field.inputType === InputType.Client,
          ),
          name: getComponentNameByComponentType(field),
          isOptional: field.isOptional,
          // For image based component the text inputed by user to generate
          // signature image is stored in the "value" key
          imageTextValue:
            field.type === FieldType.Signature ||
            field.type === FieldType.Initials
              ? field.value
              : '',
        };
      })
      .filter(isContractField);

    return formattedContractFields;
  }, [contract]);

  const handlePageImageLoad = (pageImage: SignaturePageImageData) => {
    if (pageImage.loaded) return;

    const pageData = {
      ...pageImage,
      loaded: true,
    };

    const stagedPageImages = pageImages.map((p) => {
      if (p.pageNumber === pageData.pageNumber) {
        return pageData;
      }
      return p;
    });

    setPageImages(stagedPageImages);
  };

  const isPdfLoaded = useMemo(
    () => pageImages.every((pageImage) => pageImage.loaded),
    [pageImages],
  );

  return (
    <div className={classes.root}>
      <div className="mb-5">
        <Callout
          description={t(
            ($) => $.page_contracts.contract_not_completed_description,
          )}
          variant="neutral"
        />
      </div>
      <div className={classes.container}>
        {!isPdfLoaded && <PDFSkeleton />}
        {pageImages?.length > 0 &&
          pageImages.map((pageImage) => (
            <SignaturePageImage
              key={pageImage.key}
              pageImage={pageImage}
              handlePageImageLoad={() => handlePageImageLoad(pageImage)}
            />
          ))}

        <div className={classes.fieldsContainer}>
          {pageComponents.length > 0 &&
            pageComponents.map((component) => (
              <ContractDraggableItem
                key={component.key}
                type={ContractDragItemType.Pending}
                component={component}
                disableDragging
              />
            ))}
        </div>
      </div>
    </div>
  );
};
